import moment from 'moment';
import Privileges from '../lib/constants/privileges';
import colors from 'lib/constants/resourceColors';
import invoiceBadgeStyle from 'lib/helpers/invoiceBadgeStyle';

// Accounting -> Credits. Lists ALL credits -- standalone/courtesy credits AND ordinary payments
// that still carry unapplied money -- each with an open/closed status, and lets staff create a
// standalone credit not tied to any payment.
//
// Backed by the transactions table (a credit IS a transaction; see est-back-end resources/credits.js
// and est-database migration 1785300250). The SEARCH list reads the filtered `credits_extended`
// view; CREATE posts to /credits, which stamps is_credit=TRUE server-side so the new courtesy
// credit stays out of the Deposit Slip and the Payments list.

// Table badge: called (value, row). Open = money still available to apply; Closed = fully applied
// or voided.
function creditBadgeStyle(value, data) {
    const voided = data && data.voided;
    const status = (data && data.credit_status) || value;
    if (voided) {
        return { status: 'error', text: 'Void' };
    } else if (status === 'open') {
        return { status: 'processing', text: 'Open' };
    } else if (status === 'closed') {
        return { status: 'success', text: 'Closed' };
    }
    return null;
}

// Create form: the only editable inputs when minting a standalone credit. is_credit is stamped
// server-side, transaction_number is auto-generated, payment_date defaults to today at the DB.
const createForm = [
    {key: 'customer_id',
        displayKey: ['customer_name'],
        label: 'Customer',
        required: true,
        type: 'autocomplete',
        link: { url: '/customers/:customer_id', privileges: Privileges.READ_CUSTOMERS },
        suggestFormat: (c) => ({ value: c.id, text: `${c.name}` }),
        suggestUrl: '/customers',
        suggestFields: ['id', 'name']},
    {type: 'currency', key: 'amount', label: 'Credit Amount', required: true},
    {key: 'payment_date', label: 'Credit Date', type: 'date', defaultValue: moment()},
    {key: 'memo', label: 'Reason / Memo'},
    '^file(File){file_url}',
];

// Detail view: identity + money are static (amount/customer are read-only after creation, backend
// readOnly.update). memo / payment_date / attachment stay editable so a reason or date typo can be
// corrected. Voiding a credit is done from the Payment Details screen (shared transactions row).
const viewForm = [
    {key: 'transaction_number', label: 'Credit #', static: true, readOnly: true},
    {key: 'customer_name', label: 'Customer', static: true, readOnly: true,
        link: {url: '/customers/:customer_id', privileges: Privileges.READ_CUSTOMERS}},
    {key: 'amount', label: 'Credit Amount', type: 'currency', static: true, readOnly: true},
    {key: 'available_credit', label: 'Available Credit', type: 'currency', static: true, readOnly: true},
    {key: 'payments_sum', label: 'Applied to Invoices', type: 'currency', static: true, readOnly: true},
    {key: 'payment_date', label: 'Credit Date', type: 'date'},
    {key: 'memo', label: 'Reason / Memo'},
    '^file(File){file_url}',
    {key: 'voided', label: 'Voided', static: true, readOnly: true, type: 'boolean'},
    // Where this credit's money has been applied. Reuses the transaction_invoices list (a credit
    // is a transaction), so each applied invoice line links to its invoice detail.
    {key: 'transaction_invoices',
        type: 'references',
        heading: 'Applied to Invoices',
        readPrivileges: [Privileges.LIST_TRANSACTION_INVOICES],
        dataUrl: '/lists/transaction_invoices?transaction_id=:id',
        tableProps: {
            rowURL: '/invoices/:invoice_id',
            columns: [
                {heading: 'Payment Date', accessor: 'payment_date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '110px'},
                {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Description', accessor: 'invoice_description'},
                {heading: 'Amount Applied', type: 'currency', accessor: 'amount'},
            ]
        }
    },
    // This customer's still-open invoices, so staff can jump straight from an available credit to an
    // invoice to apply it against. Reuses the shared customer_open_invoices list (already excludes
    // voided and zero-balance invoices server-side); each row links to the invoice detail.
    {key: 'customer_open_invoices',
        type: 'references',
        heading: 'Open Invoices',
        readPrivileges: [Privileges.LIST_CUSTOMER_OPEN_INVOICES],
        dataUrl: '/lists/customer_open_invoices?customer_id=:customer_id',
        tableProps: {
            rowURL: '/invoices/:id',
            columns: [
                {heading: 'Date', accessor: 'date', type: 'date', dateFormat: 'MMM DD, YYYY', cellWidth: '100px'},
                {heading: 'Invoice #', accessor: 'invoice_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Description', accessor: 'project_description'},
                {heading: 'Grand Total', type: 'currency', accessor: 'grand_total', cellWidth: '120px'},
                {heading: 'Open Balance', type: 'currency', accessor: 'open_balance', cellWidth: '120px'},
                {heading: 'Status', type: 'badge', accessor: 'invoice_status', badgePropsFunc: invoiceBadgeStyle},
            ]
        }
    },
];

export default {
    defaults: {
        name: 'Credit',
        color: colors.transaction,
        formFields: createForm,
    },
    search: {
        allowDateFilter: true,
        heading: 'Credits',
        searchBy: ['transaction number', 'customer name', 'customer number', 'memo', 'amount'],
        fields: ['id', 'transaction_number', 'amount', 'customer_name', 'available_credit', 'credit_status', 'voided', 'payment_date', 'created_at', 'updated_at'],
        // Default: all live (non-voided) credits. Open/Closed narrow by remaining balance; Voided
        // surfaces the audit rows. Each option pins voided so the list and the highlighted radio
        // agree (mirrors the Payments screen).
        filterOptions: [
            {label: 'All', filters: {voided: false}, default: true},
            {label: 'Open', filters: {voided: false, credit_status: 'open'}},
            {label: 'Closed', filters: {voided: false, credit_status: 'closed'}},
            {label: 'Voided', filters: {voided: true}},
        ],
        links: [
            {url: '/credits/new', icon: 'plus', title: 'New Credit', requiredPrivileges: [Privileges.CREATE_CREDITS]},
        ],
        tableProps: {
            rowURL: '/credits/:id',
            columns: [
                {heading: 'Credit Date', cellWidth: '110px', dateFormat: 'MMM DD, YYYY', type: 'date', accessor: 'payment_date'},
                {cellWidth: '150px', accessor: 'customer_name'},
                {heading: 'Credit #', accessor: 'transaction_number', cellWidth: '110px', style: 'bold'},
                {heading: 'Amount', accessor: 'amount', type: 'currency', cellWidth: '110px'},
                {heading: 'Available', accessor: 'available_credit', type: 'currency', cellWidth: '110px'},
                {heading: 'Status', type: 'badge', accessor: 'credit_status', badgePropsFunc: creditBadgeStyle},
            ]
        }
    },
    view: {
        title: () => 'Credit Details',
        formFields: viewForm,
        // Share the transaction remark stream (same underlying row / global_ref).
        remarks: {
            header: (t) => t.transaction_number,
            object_type: 'transaction',
            object_id_key: 'id',
        },
        // A credit is append-only: no hard delete (voiding is done from Payment Details).
        enableDelete: false,
    },
}
